import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { slugify } from "@eaub/shared";
import type {
  ContentType,
  CreateContentTypeInput,
  FieldDef,
} from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";
import { FieldRowEditor } from "../components/FieldRowEditor";
import type { FieldErrors } from "../components/FieldRowEditor";

type FieldConfig = FieldDef["config"];

interface LoadResult {
  contentTypes: ContentType[];
  editing: ContentType | null;
}

const KEY_RE = /^[a-z][a-z0-9_]*$/;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function newField(): FieldDef {
  return { key: "", label: "", kind: "text", required: false, config: {} };
}

function defaultFields(): FieldDef[] {
  return [{ key: "title", label: "Title", kind: "text", required: true, config: {} }];
}

function cleanConfig(field: FieldDef): FieldConfig {
  const c = field.config;
  switch (field.kind) {
    case "text":
    case "richtext": {
      const out: FieldConfig = {};
      if (typeof c.minLength === "number") out.minLength = c.minLength;
      if (typeof c.maxLength === "number") out.maxLength = c.maxLength;
      return out;
    }
    case "number": {
      const out: FieldConfig = {};
      if (typeof c.min === "number") out.min = c.min;
      if (typeof c.max === "number") out.max = c.max;
      if (c.integer) out.integer = true;
      return out;
    }
    case "media": {
      return c.multiple ? { multiple: true } : {};
    }
    case "reference": {
      const out: FieldConfig = {};
      if (c.multiple) out.multiple = true;
      if (c.refTypeId) out.refTypeId = c.refTypeId;
      return out;
    }
    default:
      return {};
  }
}

interface ValidationResult {
  valid: boolean;
  formError?: string;
  titleError?: string;
  fieldErrors: Record<number, FieldErrors>;
}

function validate(
  name: string,
  slug: string,
  titleFieldKey: string,
  fields: FieldDef[],
): ValidationResult {
  const fieldErrors: Record<number, FieldErrors> = {};
  const seen = new Set<string>();

  fields.forEach((f, i) => {
    const fe: FieldErrors = {};
    if (!f.key) fe.key = "Key is required";
    else if (!KEY_RE.test(f.key)) fe.key = "Must be snake_case (start with a letter)";
    else if (seen.has(f.key)) fe.key = "Duplicate key";
    if (f.key) seen.add(f.key);

    if (!f.label.trim()) fe.label = "Label is required";
    if (f.kind === "reference" && !f.config.refTypeId) fe.refTypeId = "Choose a referenced type";

    if (fe.key || fe.label || fe.refTypeId) fieldErrors[i] = fe;
  });

  let formError: string | undefined;
  if (!name.trim()) formError = "Name is required";
  else if (!SLUG_RE.test(slug)) formError = "Slug must be lowercase words separated by hyphens";
  else if (fields.length === 0) formError = "Add at least one field";

  let titleError: string | undefined;
  if (!titleFieldKey) titleError = "Choose a title field";
  else if (!fields.some((f) => f.key === titleFieldKey)) {
    titleError = "Title field must match one of the field keys";
  }

  const valid = !formError && !titleError && Object.keys(fieldErrors).length === 0;
  return { valid, formError, titleError, fieldErrors };
}

export function ContentTypeBuilder() {
  const api = useApi();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const load = useCallback(async (): Promise<LoadResult> => {
    const contentTypes = await api.listContentTypes();
    let editing: ContentType | null = null;
    if (id) {
      editing = contentTypes.find((c) => c.id === id) ?? (await api.getContentType(id));
    }
    return { contentTypes, editing };
  }, [api, id]);

  const { data, loading, error, reload } = useAsync<LoadResult>(load, [load]);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [titleFieldKey, setTitleFieldKey] = useState("");
  const [fields, setFields] = useState<FieldDef[]>(defaultFields);
  const [hydrated, setHydrated] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [showErrors, setShowErrors] = useState(false);

  // Hydrate the form once data is available.
  useEffect(() => {
    if (!data || hydrated) return;
    if (data.editing) {
      setName(data.editing.name);
      setSlug(data.editing.slug);
      setSlugTouched(true);
      setTitleFieldKey(data.editing.titleFieldKey);
      setFields(data.editing.fields);
    }
    setHydrated(true);
  }, [data, hydrated]);

  const validation = useMemo(
    () => validate(name, slug, titleFieldKey, fields),
    [name, slug, titleFieldKey, fields],
  );

  const keyOptions = useMemo(
    () => fields.map((f) => f.key).filter((k) => k.length > 0),
    [fields],
  );

  const onNameChange = (value: string) => {
    setName(value);
    if (!slugTouched) setSlug(slugify(value));
  };

  const updateField = (index: number, next: FieldDef) => {
    setFields((prev) => prev.map((f, i) => (i === index ? next : f)));
  };

  const removeField = (index: number) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
  };

  const moveField = (index: number, delta: number) => {
    setFields((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const copy = [...prev];
      const a = copy[index];
      const b = copy[target];
      if (!a || !b) return prev;
      copy[index] = b;
      copy[target] = a;
      return copy;
    });
  };

  const addField = () => setFields((prev) => [...prev, newField()]);

  const onSubmit = async () => {
    setShowErrors(true);
    setSubmitError(undefined);
    if (!validation.valid) return;

    const input: CreateContentTypeInput = {
      name: name.trim(),
      slug: slug.trim(),
      titleFieldKey,
      fields: fields.map((f) => ({
        key: f.key,
        label: f.label.trim(),
        kind: f.kind,
        required: f.required,
        config: cleanConfig(f),
      })),
    };

    setSubmitting(true);
    try {
      if (isEdit && id) {
        await api.updateContentType(id, input);
      } else {
        await api.createContentType(input);
      }
      navigate("/content-types");
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner label="Loading…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;

  const contentTypes = data?.contentTypes ?? [];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">{isEdit ? "Edit content type" : "New content type"}</h1>
          <p className="page__subtitle">Define fields and the title field for this type.</p>
        </div>
      </div>

      {submitError ? <ErrorBanner title="Could not save" message={submitError} /> : null}
      {showErrors && validation.formError ? (
        <ErrorBanner title="Please fix the form" message={validation.formError} />
      ) : null}

      <form
        className="builder-form"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        <section className="panel">
          <div className="form-grid">
            <div className="form-field">
              <label className="form-field__label" htmlFor="ct-name">
                Name
              </label>
              <input
                id="ct-name"
                className="input"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label className="form-field__label" htmlFor="ct-slug">
                Slug
              </label>
              <input
                id="ct-slug"
                className="input"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
              />
            </div>
            <div className="form-field">
              <label className="form-field__label" htmlFor="ct-title">
                Title field
              </label>
              <select
                id="ct-title"
                className={showErrors && validation.titleError ? "input input--error" : "input"}
                value={titleFieldKey}
                onChange={(e) => setTitleFieldKey(e.target.value)}
              >
                <option value="">— choose a field —</option>
                {keyOptions.map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
              {showErrors && validation.titleError ? (
                <span className="form-field__error">{validation.titleError}</span>
              ) : null}
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">Fields</h2>
            <button type="button" className="btn btn--secondary btn--sm" onClick={addField}>
              Add field
            </button>
          </div>

          {fields.length === 0 ? (
            <p className="muted">No fields yet. Add at least one.</p>
          ) : (
            <div className="field-list">
              {fields.map((field, index) => (
                <FieldRowEditor
                  key={index}
                  field={field}
                  index={index}
                  total={fields.length}
                  contentTypes={contentTypes}
                  errors={showErrors ? validation.fieldErrors[index] : undefined}
                  onChange={(next) => updateField(index, next)}
                  onRemove={() => removeField(index)}
                  onMoveUp={() => moveField(index, -1)}
                  onMoveDown={() => moveField(index, 1)}
                />
              ))}
            </div>
          )}
        </section>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => navigate("/content-types")}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Create content type"}
          </button>
        </div>
      </form>
    </div>
  );
}
